import {
  type Cue,
  detectFormat,
  parseTranscript,
  type TranscriptFormat,
} from "@/lib/tool-kit/qualitative-text/transcript-parse";

export type FillerLevel = "none" | "disfluencies" | "intelligent";
export type SpeakerStyle = "labels" | "markdown" | "none";
export type TimeStyle = "none" | "turn";

export interface TranscriptOptions {
  /** `null` detects the layout from the text. */
  format: TranscriptFormat | null;
  speakers: SpeakerStyle;
  timestamps: TimeStyle;
  /** Join consecutive cues from the same speaker into one turn. */
  mergeTurns: boolean;
  /** Start a new paragraph after this many sentences in a long turn; 0 never. */
  paragraphSentences: number;
  fillers: FillerLevel;
  /** Collapse an immediate repeat: "I I think" becomes "I think". */
  repeats: boolean;
  /** Remove [Music], [Applause] and ♪ lines. */
  soundCues: boolean;
  /** Extra words or phrases to remove, separated by commas or line breaks. */
  customFillers: string;
}

export interface Turn {
  start: number | null;
  speaker: string | null;
  text: string;
}

export interface SpeakerStats {
  speaker: string;
  turns: number;
  words: number;
}

export interface TranscriptResult {
  format: TranscriptFormat;
  output: string;
  cues: number;
  turns: number;
  wordsBefore: number;
  wordsAfter: number;
  removed: number;
  speakers: SpeakerStats[];
}

const MS_SECOND = 1000;
const MS_HOUR = 3_600_000;
const MS_MINUTE = 60_000;
const RE_WORD = /[\p{L}\p{N}][\p{L}\p{N}'’-]*/gu;
const RE_SPACES = /[ \t]+/g;
const RE_SPACE_BEFORE_PUNCT = /\s+([,.;:!?…])/g;
const RE_ESCAPE = /[.*+?^${}()|[\]\\]/g;
const RE_LIST_SPLIT = /[,\n]+/;
const RE_SOUND_BRACKET = /\[[^\]\n]*\]/g;
const RE_MUSIC = /[♪♫][^♪♫\n]*[♪♫]?/g;
const RE_LEADING_PUNCT = /^[\s,;:.…]+/;
const RE_SENTENCE_BREAK = /(?<=[.!?…]["”’)]?)\s+(?=["“‘([]?\p{Lu})/u;
const MARK = "\u0000";
const DISFLUENCY = "(?:u[mh]+|er+m*|h?m{2,}|hm)";
const WORD_EDGE_BEFORE = "(?<![\\p{L}\\p{N}'’-])";
const WORD_EDGE_AFTER = "(?![\\p{L}\\p{N}'’-])";

function wordCount(text: string): number {
  return text.match(RE_WORD)?.length ?? 0;
}

/** Remove words (given as regex source), keeping the sentence and its commas tidy. */
function removeWords(
  text: string,
  alternatives: string
): { text: string; removed: number } {
  if (alternatives === "") {
    return { text, removed: 0 };
  }
  const word = `${WORD_EDGE_BEFORE}(?:${alternatives})${WORD_EDGE_AFTER}`;
  const find = new RegExp(word, "giu");
  const removed = text.match(find)?.length ?? 0;
  if (removed === 0) {
    return { text, removed: 0 };
  }
  const flanked = new RegExp(`,\\s*${word}\\s*,`, "giu");
  const closing = new RegExp(`,\\s*${word}(?=[.!?…])`, "giu");
  const opening = new RegExp(`(^|[.!?…]\\s+)${word}[\\s,]*(\\p{Ll})`, "giu");
  const result = text
    .replace(flanked, ",")
    .replace(closing, "")
    .replace(
      opening,
      (_all: string, lead: string, next: string) => lead + next.toUpperCase()
    )
    .replace(find, "")
    .replace(RE_SPACES, " ")
    .replace(RE_SPACE_BEFORE_PUNCT, "$1")
    .trim();
  return { text: result, removed };
}

const RE_PHRASE_FLANKED = /,\s*(?:you know|I mean)\s*,/gi;
const RE_PHRASE_OPENING = /(^|[.!?…]\s+)(?:you know|I mean)\s*,\s*(\p{Ll})/giu;
const RE_PHRASE_CLOSING = /,\s*(?:you know|I mean)(?=[.!?…])/gi;
const RE_REPEAT =
  /(?<![\p{L}\p{N}'’-])(\p{L}[\p{L}'’-]*)(?:\s+\1(?![\p{L}\p{N}'’-]))+/giu;

/** "you know" and "I mean", only where commas or the sentence edge set them apart. */
function removeParentheticals(text: string): { text: string; removed: number } {
  let removed = 0;
  const count = (replacement: string) => () => {
    removed += 1;
    return replacement;
  };
  const result = text
    .replace(RE_PHRASE_FLANKED, count(","))
    .replace(RE_PHRASE_OPENING, (_all: string, lead: string, next: string) => {
      removed += 1;
      return lead + next.toUpperCase();
    })
    .replace(RE_PHRASE_CLOSING, count(""));
  return { text: result, removed };
}

function customAlternatives(list: string): string {
  return list
    .split(RE_LIST_SPLIT)
    .map((item) => item.trim())
    .filter((item) => item !== "")
    .sort((a, b) => b.length - a.length)
    .map((item) => item.replace(RE_ESCAPE, "\\$&").replace(RE_SPACES, "\\s+"))
    .join("|");
}

/** Apply the cleaning options to one run of text; returns what was taken out. */
export function cleanText(
  input: string,
  options: TranscriptOptions
): { text: string; removed: number } {
  let text = input;
  let removed = 0;
  const apply = (step: { text: string; removed: number }) => {
    text = step.text;
    removed += step.removed;
  };
  if (options.soundCues) {
    const before = text;
    text = text
      .replace(RE_SOUND_BRACKET, MARK)
      .replace(RE_MUSIC, MARK)
      .replace(new RegExp(MARK, "g"), "")
      .replace(RE_SPACES, " ")
      .replace(RE_SPACE_BEFORE_PUNCT, "$1")
      .trim();
    removed += before === text ? 0 : 1;
  }
  if (options.fillers !== "none") {
    apply(removeWords(text, DISFLUENCY));
  }
  if (options.fillers === "intelligent") {
    apply(removeParentheticals(text));
  }
  apply(removeWords(text, customAlternatives(options.customFillers)));
  if (options.repeats) {
    const before = text;
    text = text.replace(RE_REPEAT, "$1");
    removed += before === text ? 0 : 1;
  }
  return { text: text.replace(RE_LEADING_PUNCT, "").trim(), removed };
}

/** Rolling captions repeat the last line of the cue before: drop the repeat. */
function dropRollingRepeats(cues: readonly Cue[]): Cue[] {
  const result: Cue[] = [];
  let previous: Cue | undefined;
  for (const cue of cues) {
    let lines = cue.lines;
    const last = previous?.lines.at(-1);
    if (last !== undefined && lines[0] === last) {
      lines = lines.slice(1);
    }
    previous = cue;
    if (lines.length > 0) {
      result.push({ ...cue, lines });
    }
  }
  return result;
}

/**
 * Group cues into turns first and clean the joined text afterwards, so a filler at the
 * start of one caption ("…in 2019," / "uh, after my degree.") is judged in its sentence.
 * A caption that is nothing but a sound cue or a filler is dropped before grouping, so
 * it neither starts a turn nor lends it a timestamp.
 */
function buildTurns(
  cues: readonly Cue[],
  options: TranscriptOptions
): { turns: Turn[]; removed: number } {
  const grouped: Turn[] = [];
  let removed = 0;
  for (const cue of cues) {
    const raw = cue.lines.join(" ");
    if (cleanText(raw, options).text === "") {
      removed += raw.trim() === "" ? 0 : 1;
      continue;
    }
    const last = grouped.at(-1);
    if (options.mergeTurns && last && last.speaker === cue.speaker) {
      last.text = `${last.text} ${raw}`;
    } else {
      grouped.push({ start: cue.start, speaker: cue.speaker, text: raw });
    }
  }
  const turns = grouped.map((turn) => {
    const cleaned = cleanText(turn.text, options);
    removed += cleaned.removed;
    return { ...turn, text: cleaned.text };
  });
  return { turns, removed };
}

export function formatClock(ms: number, withHours: boolean): string {
  const total = Math.floor(ms / MS_SECOND);
  const hours = Math.floor(ms / MS_HOUR);
  const minutes = Math.floor((ms % MS_HOUR) / MS_MINUTE);
  const seconds = total % 60;
  const two = (n: number) => String(n).padStart(2, "0");
  return withHours
    ? `${hours}:${two(minutes)}:${two(seconds)}`
    : `${Math.floor(ms / MS_MINUTE)}:${two(seconds)}`;
}

function paragraphsOf(text: string, every: number): string[] {
  if (every <= 0) {
    return [text];
  }
  const sentences = text.split(RE_SENTENCE_BREAK);
  const paragraphs: string[] = [];
  for (let i = 0; i < sentences.length; i += every) {
    paragraphs.push(sentences.slice(i, i + every).join(" "));
  }
  return paragraphs;
}

function renderTurn(turn: Turn, options: TranscriptOptions, hours: boolean) {
  const paragraphs = paragraphsOf(turn.text, options.paragraphSentences);
  const stamp =
    options.timestamps === "turn" && turn.start !== null
      ? `[${formatClock(turn.start, hours)}] `
      : "";
  let label = "";
  if (turn.speaker && options.speakers === "labels") {
    label = `${turn.speaker}: `;
  } else if (turn.speaker && options.speakers === "markdown") {
    label = `**${turn.speaker}:** `;
  }
  return paragraphs
    .map((paragraph, index) => (index === 0 ? stamp + label : "") + paragraph)
    .join("\n\n");
}

function speakerStats(turns: readonly Turn[]): SpeakerStats[] {
  const stats = new Map<string, SpeakerStats>();
  for (const turn of turns) {
    if (!turn.speaker) {
      continue;
    }
    const entry = stats.get(turn.speaker) ?? {
      speaker: turn.speaker,
      turns: 0,
      words: 0,
    };
    entry.turns += 1;
    entry.words += wordCount(turn.text);
    stats.set(turn.speaker, entry);
  }
  return [...stats.values()].sort((a, b) => b.words - a.words);
}

export const DEFAULT_TRANSCRIPT_OPTIONS: TranscriptOptions = {
  format: null,
  speakers: "labels",
  timestamps: "none",
  mergeTurns: true,
  paragraphSentences: 0,
  fillers: "none",
  repeats: false,
  soundCues: true,
  customFillers: "",
};

export function cleanTranscript(
  input: string,
  options: TranscriptOptions
): TranscriptResult {
  const format = options.format ?? detectFormat(input);
  const cues = dropRollingRepeats(parseTranscript(input, format));
  const { turns, removed } = buildTurns(cues, options);
  const hours = turns.some((turn) => (turn.start ?? 0) >= MS_HOUR);
  const output = turns
    .map((turn) => renderTurn(turn, options, hours))
    .join(options.mergeTurns ? "\n\n" : "\n");
  return {
    format,
    output,
    cues: cues.length,
    turns: turns.length,
    wordsBefore: wordCount(cues.map((cue) => cue.lines.join(" ")).join(" ")),
    wordsAfter: wordCount(turns.map((turn) => turn.text).join(" ")),
    removed,
    speakers: speakerStats(turns),
  };
}
